import React, { useState, useMemo, useCallback } from 'react'
import { useTasks } from '../hooks/useTasks'
import { PartnerTask, TaskStatus, TaskPriority } from '../types/partner'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Checkbox } from '../components/ui/checkbox'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Label } from '../components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet'
import {
  Plus,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  X,
  Menu,
  CheckCircle,
  Trash2,
  Grid3X3,
} from 'lucide-react'

// --- Constants ---

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const CATEGORY_COLORS: Record<string, string> = {
  Reports: 'bg-sky-500',
  Onboarding: 'bg-emerald-500',
  Marketplace: 'bg-amber-500',
  Sales: 'bg-violet-500',
  Support: 'bg-rose-500',
  Meetings: 'bg-teal-500',
  Project: 'bg-indigo-500',
  Other: 'bg-slate-500',
}

const CATEGORY_TEXT_COLORS: Record<string, string> = {
  Reports: 'text-sky-700 bg-sky-50',
  Onboarding: 'text-emerald-700 bg-emerald-50',
  Marketplace: 'text-amber-700 bg-amber-50',
  Sales: 'text-violet-700 bg-violet-50',
  Support: 'text-rose-700 bg-rose-50',
  Meetings: 'text-teal-700 bg-teal-50',
  Project: 'text-indigo-700 bg-indigo-50',
  Other: 'text-slate-700 bg-slate-50',
}

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; variant: 'destructive' | 'warning' | 'info' | 'success' | 'secondary' }> = {
  urgent: { label: 'Urgent', variant: 'destructive' },
  high: { label: 'High', variant: 'destructive' },
  medium: { label: 'Medium', variant: 'warning' },
  normal: { label: 'Normal', variant: 'info' },
  low: { label: 'Low', variant: 'success' },
}

const STATUS_CONFIG: Record<TaskStatus, { label: string }> = {
  new: { label: 'New' },
  pending: { label: 'Pending' },
  inprogress: { label: 'In Progress' },
  completed: { label: 'Completed' },
}

const CATEGORIES = ['Reports', 'Onboarding', 'Marketplace', 'Sales', 'Support', 'Meetings', 'Project', 'Other']

function getCategoryColor(category?: string): string {
  return CATEGORY_COLORS[category ?? ''] ?? 'bg-slate-400'
}

function getCategoryTextColor(category?: string): string {
  return CATEGORY_TEXT_COLORS[category ?? ''] ?? 'text-slate-700 bg-slate-50'
}

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function toDateKeyFromStr(s: string): string {
  return s.slice(0, 10)
}

// --- Calendar Grid Helpers ---

interface CalendarDay {
  date: Date
  dateKey: string
  isCurrentMonth: boolean
  isToday: boolean
}

function getCalendarDays(year: number, month: number): CalendarDay[] {
  const today = new Date()
  const todayKey = toDateKey(today)
  const firstDay = new Date(year, month, 1)
  const startDow = firstDay.getDay()
  const start = new Date(year, month, 1 - startDow)

  const days: CalendarDay[] = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)
    const dk = toDateKey(d)
    days.push({
      date: d,
      dateKey: dk,
      isCurrentMonth: d.getMonth() === month,
      isToday: dk === todayKey,
    })
  }
  return days
}

// --- Mini Calendar ---

interface MiniCalendarProps {
  viewYear: number
  viewMonth: number
  selectedDate: string | null
  onSelectDate: (dateKey: string) => void
  onNavigate: (year: number, month: number) => void
  taskDateKeys: Set<string>
}

function MiniCalendar({ viewYear, viewMonth, selectedDate, onSelectDate, onNavigate, taskDateKeys }: MiniCalendarProps) {
  const [miniYear, setMiniYear] = useState(viewYear)
  const [miniMonth, setMiniMonth] = useState(viewMonth)

  React.useEffect(() => {
    setMiniYear(viewYear)
    setMiniMonth(viewMonth)
  }, [viewYear, viewMonth])

  const days = useMemo(() => getCalendarDays(miniYear, miniMonth), [miniYear, miniMonth])

  const prevMonth = () => {
    if (miniMonth === 0) { setMiniYear(y => y - 1); setMiniMonth(11); }
    else setMiniMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (miniMonth === 11) { setMiniYear(y => y + 1); setMiniMonth(0); }
    else setMiniMonth(m => m + 1)
  }

  return (
    <div className="px-4 pb-4">
      <div className="flex items-center justify-between mb-2">
        <button onClick={prevMonth} className="p-1 rounded hover:bg-slate-100" aria-label="Previous month">
          <ChevronLeft className="h-3.5 w-3.5 text-slate-500" />
        </button>
        <span className="text-xs font-semibold text-slate-700">
          {MONTH_NAMES[miniMonth]} {miniYear}
        </span>
        <button onClick={nextMonth} className="p-1 rounded hover:bg-slate-100" aria-label="Next month">
          <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0">
        {DAY_NAMES.map(d => (
          <div key={d} className="text-[10px] font-medium text-slate-400 text-center py-1">{d[0]}</div>
        ))}
        {days.map((day, i) => {
          const isSelected = selectedDate === day.dateKey
          const hasTasks = taskDateKeys.has(day.dateKey)
          return (
            <button
              key={i}
              onClick={() => {
                onSelectDate(day.dateKey)
                if (day.date.getMonth() !== viewMonth || day.date.getFullYear() !== viewYear) {
                  onNavigate(day.date.getFullYear(), day.date.getMonth())
                }
              }}
              className={`relative h-7 w-7 mx-auto text-[11px] rounded-full flex items-center justify-center transition-colors
                ${!day.isCurrentMonth ? 'text-slate-300' : 'text-slate-700'}
                ${day.isToday ? 'ring-1 ring-orange-400 font-bold' : ''}
                ${isSelected ? 'bg-orange-500 text-white font-bold ring-0' : 'hover:bg-slate-100'}
              `}
            >
              {day.date.getDate()}
              {hasTasks && !isSelected && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-orange-400" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// --- Calendar Sidebar ---

interface SidebarProps {
  categories: string[]
  enabledCategories: Set<string>
  onToggleCategory: (cat: string) => void
  onToggleAll: () => void
  allEnabled: boolean
  upcomingTasks: PartnerTask[]
  onSelectDate: (dateKey: string) => void
  onNewEvent: () => void
  open: boolean
  onClose: () => void
  viewYear: number
  viewMonth: number
  selectedDate: string | null
  onNavigate: (year: number, month: number) => void
  taskDateKeys: Set<string>
}

function CalendarSidebar({
  categories, enabledCategories, onToggleCategory, onToggleAll, allEnabled,
  upcomingTasks, onSelectDate, onNewEvent, open, onClose,
  viewYear, viewMonth, selectedDate, onNavigate, taskDateKeys,
}: SidebarProps) {
  const content = (
    <div className="flex flex-col h-full">
      <div className="p-4 flex items-center justify-between border-b border-slate-200">
        <h4 className="font-bold text-slate-900">Calendar</h4>
        <button onClick={onClose} className="lg:hidden p-1 rounded hover:bg-slate-100" aria-label="Close sidebar">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-4">
        <Button className="w-full" onClick={() => { onNewEvent(); onClose(); }}>
          <Calendar className="h-4 w-4 mr-2" />
          New Event
        </Button>
      </div>

      <MiniCalendar
        viewYear={viewYear}
        viewMonth={viewMonth}
        selectedDate={selectedDate}
        onSelectDate={onSelectDate}
        onNavigate={onNavigate}
        taskDateKeys={taskDateKeys}
      />

      <div className="flex-1 overflow-y-auto border-t border-slate-200">
        {/* Category Filters */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center gap-2 mb-3">
            <Checkbox
              checked={allEnabled}
              onCheckedChange={onToggleAll}
              id="cal-view-all"
            />
            <label htmlFor="cal-view-all" className="text-xs font-semibold text-slate-700 cursor-pointer">
              View All Calendars
            </label>
          </div>
          <div className="space-y-1.5">
            {categories.map(cat => (
              <div key={cat} className="flex items-center gap-2">
                <button
                  onClick={() => onToggleCategory(cat)}
                  className="flex items-center gap-2 w-full text-left px-1 py-0.5 rounded hover:bg-slate-50"
                >
                  <span className={`h-3 w-3 rounded-sm flex-shrink-0 ${getCategoryColor(cat)} ${!enabledCategories.has(cat) ? 'opacity-30' : ''}`} />
                  <span className={`text-xs ${enabledCategories.has(cat) ? 'text-slate-700' : 'text-slate-400'}`}>
                    {cat}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="px-4 pt-4">
          <p className="text-[10px] font-bold uppercase text-slate-400 tracking-wider mb-3">Events & Schedules</p>
          {upcomingTasks.length === 0 && (
            <p className="text-xs text-slate-400 italic">No upcoming events</p>
          )}
          {upcomingTasks.map(task => {
            const dueDate = task.dueDate ? new Date(task.dueDate) : null
            return (
              <button
                key={task.id}
                onClick={() => {
                  if (task.dueDate) onSelectDate(toDateKeyFromStr(task.dueDate))
                }}
                className="w-full text-left p-3 border-t border-slate-100 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  {dueDate && (
                    <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex flex-col items-center justify-center ${getCategoryTextColor(task.category)}`}>
                      <span className="text-sm font-bold leading-tight">{dueDate.getDate()}</span>
                      <span className="text-[9px] font-semibold uppercase leading-tight">
                        {MONTH_NAMES[dueDate.getMonth()].slice(0, 3)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{task.title}</p>
                    {task.category && (
                      <span className="text-[10px] text-slate-500">{task.category}</span>
                    )}
                    <p className="text-[10px] text-slate-400 truncate mt-0.5">{task.description}</p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )

  return (
    <>
      <div className="hidden lg:flex w-[280px] flex-shrink-0 border-r border-slate-200 bg-white">
        {content}
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/40" onClick={onClose} />
          <div className="relative w-[300px] bg-white shadow-xl z-50">
            {content}
          </div>
        </div>
      )}
    </>
  )
}

// --- Month Grid ---

interface MonthGridProps {
  year: number
  month: number
  tasksByDate: Map<string, PartnerTask[]>
  selectedDate: string | null
  onSelectDate: (dateKey: string) => void
  onSelectTask: (task: PartnerTask) => void
  onAddEvent: (dateKey: string) => void
}

const MAX_VISIBLE_TASKS = 3

function MonthGrid({ year, month, tasksByDate, selectedDate, onSelectDate, onSelectTask, onAddEvent }: MonthGridProps) {
  const days = useMemo(() => getCalendarDays(year, month), [year, month])

  return (
    <div className="flex-1 flex flex-col">
      {/* Day-of-week header */}
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {DAY_NAMES.map(d => (
          <div key={d} className="px-2 py-2 text-xs font-semibold text-slate-500 text-center">{d}</div>
        ))}
      </div>
      {/* Calendar grid */}
      <div className="grid grid-cols-7 flex-1 auto-rows-fr">
        {days.map((day, i) => {
          const tasks = tasksByDate.get(day.dateKey) ?? []
          const isSelected = selectedDate === day.dateKey
          const overflow = tasks.length > MAX_VISIBLE_TASKS ? tasks.length - MAX_VISIBLE_TASKS : 0

          return (
            <div
              key={i}
              className={`border-b border-r border-slate-100 p-1 min-h-[90px] cursor-pointer transition-colors group
                ${!day.isCurrentMonth ? 'bg-slate-50/50' : 'bg-white hover:bg-orange-50/30'}
                ${isSelected ? 'bg-orange-50 ring-1 ring-inset ring-orange-200' : ''}
              `}
              onClick={() => { onSelectDate(day.dateKey); onAddEvent(day.dateKey); }}
            >
              {/* Day number */}
              <div className="flex items-center justify-between mb-0.5">
                <span
                  className={`text-xs font-medium h-6 w-6 flex items-center justify-center rounded-full
                    ${!day.isCurrentMonth ? 'text-slate-300' : 'text-slate-600'}
                    ${day.isToday ? 'bg-orange-500 text-white font-bold' : ''}
                  `}
                >
                  {day.date.getDate()}
                </span>
              </div>
              {/* Task pills */}
              <div className="space-y-0.5">
                {tasks.slice(0, MAX_VISIBLE_TASKS).map(task => (
                  <button
                    key={task.id}
                    onClick={(e) => { e.stopPropagation(); onSelectTask(task); }}
                    className={`w-full text-left text-[10px] leading-tight px-1.5 py-0.5 rounded truncate font-medium transition-opacity hover:opacity-80 ${getCategoryTextColor(task.category)}`}
                    title={task.title}
                  >
                    <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 ${getCategoryColor(task.category)}`} />
                    {task.title}
                  </button>
                ))}
                {overflow > 0 && (
                  <div className="text-[10px] text-slate-400 font-medium px-1.5">+{overflow} more</div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// --- Add Event Dialog ---

interface EventFormData {
  title: string
  description: string
  dueDate: string
  status: TaskStatus
  priority: TaskPriority
  category: string
  assigneeName: string
}

const EMPTY_EVENT_FORM: EventFormData = {
  title: '',
  description: '',
  dueDate: '',
  status: 'new',
  priority: 'normal',
  category: '',
  assigneeName: '',
}

function AddEventDialog({
  open, onClose, onSave, initialDate,
}: {
  open: boolean
  onClose: () => void
  onSave: (data: EventFormData) => void
  initialDate?: string
}) {
  const [form, setForm] = useState<EventFormData>({ ...EMPTY_EVENT_FORM, dueDate: initialDate ?? '' })

  React.useEffect(() => {
    if (open) setForm({ ...EMPTY_EVENT_FORM, dueDate: initialDate ?? '' })
  }, [open, initialDate])

  const update = (field: keyof EventFormData, value: string) =>
    setForm(f => ({ ...f, [field]: value }))

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>New Event</DialogTitle>
          <DialogDescription>Create a new calendar event for your team.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label htmlFor="evt-title">Title</Label>
            <Input id="evt-title" placeholder="Event title" value={form.title} onChange={e => update('title', e.target.value)} />
          </div>
          <div>
            <Label htmlFor="evt-desc">Description</Label>
            <Textarea id="evt-desc" placeholder="Brief description..." rows={3} value={form.description} onChange={e => update('description', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="evt-date">Date</Label>
              <Input id="evt-date" type="date" value={form.dueDate} onChange={e => update('dueDate', e.target.value)} />
            </div>
            <div>
              <Label>Priority</Label>
              <select
                value={form.priority}
                onChange={e => update('priority', e.target.value)}
                aria-label="Event priority"
                className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Category</Label>
              <select
                value={form.category}
                onChange={e => update('category', e.target.value)}
                aria-label="Event category"
                className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                <option value="">None</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label htmlFor="evt-assignee">Assignee</Label>
              <Input id="evt-assignee" placeholder="Name" value={form.assigneeName} onChange={e => update('assigneeName', e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!form.title.trim()} onClick={() => { onSave(form); onClose(); }}>
            Create Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// --- Task Detail Sheet ---

function TaskDetailSheet({
  task, open, onClose, onUpdate, onDelete, onToggleComplete,
}: {
  task: PartnerTask | null
  open: boolean
  onClose: () => void
  onUpdate: (id: string, updates: Partial<PartnerTask>) => void
  onDelete: (id: string) => void
  onToggleComplete: (id: string) => void
}) {
  const [newSubtask, setNewSubtask] = useState('')

  if (!task) return null

  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return
    onUpdate(task.id, { subtasks: [...task.subtasks, { id: crypto.randomUUID(), title: newSubtask.trim(), completed: false }] })
    setNewSubtask('')
  }

  const completedSubtasks = task.subtasks.filter(s => s.completed).length

  return (
    <Sheet open={open} onOpenChange={v => { if (!v) onClose() }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4 border-b border-slate-200">
          <SheetTitle className="text-lg">{task.title}</SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            {task.dueDate && `Due ${new Date(task.dueDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · `}
            {task.category && <Badge variant="secondary" size="sm" className="mr-1">{task.category}</Badge>}
            <Badge variant={PRIORITY_CONFIG[task.priority].variant} size="sm">{PRIORITY_CONFIG[task.priority].label}</Badge>
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 pt-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Status</Label>
              <select
                value={task.status}
                onChange={e => onUpdate(task.id, { status: e.target.value as TaskStatus, completed: e.target.value === 'completed' })}
                aria-label="Task status"
                className="flex h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Priority</Label>
              <select
                value={task.priority}
                onChange={e => onUpdate(task.id, { priority: e.target.value as TaskPriority })}
                aria-label="Task priority"
                className="flex h-9 w-full rounded-lg border border-slate-300 bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              >
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Category</Label>
              {task.category ? <Badge variant="secondary">{task.category}</Badge> : <span className="text-sm text-slate-400">None</span>}
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Assignee</Label>
              {task.assigneeName ? (
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center">
                    <span className="text-[9px] font-bold text-orange-700">
                      {task.assigneeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <span className="text-sm text-slate-700">{task.assigneeName}</span>
                </div>
              ) : (
                <span className="text-sm text-slate-400">Unassigned</span>
              )}
            </div>
          </div>

          {task.dueDate && (
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Due Date</Label>
              <span className="text-sm text-slate-700">{new Date(task.dueDate).toLocaleDateString()}</span>
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-500 mb-1.5 block">Description</Label>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {task.description || <span className="text-slate-400 italic">No description</span>}
            </p>
          </div>

          {/* Subtasks */}
          <div>
            <Label className="text-xs text-slate-500 mb-3 block">
              Subtasks {task.subtasks.length > 0 && `(${completedSubtasks}/${task.subtasks.length})`}
            </Label>
            {task.subtasks.length > 0 && (
              <div className="mb-3 rounded-lg border border-slate-200 divide-y divide-slate-100">
                {task.subtasks.map(sub => (
                  <div key={sub.id} className="flex items-center gap-2.5 px-3 py-2 group/sub">
                    <Checkbox
                      checked={sub.completed}
                      onCheckedChange={() => {
                        const updated = task.subtasks.map(s => s.id === sub.id ? { ...s, completed: !s.completed } : s)
                        onUpdate(task.id, { subtasks: updated })
                      }}
                    />
                    <span className={`flex-1 text-sm ${sub.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                      {sub.title}
                    </span>
                    <button
                      onClick={() => onUpdate(task.id, { subtasks: task.subtasks.filter(s => s.id !== sub.id) })}
                      className="opacity-0 group-hover/sub:opacity-100 p-0.5 rounded hover:bg-slate-100 transition-all"
                      aria-label="Remove subtask"
                    >
                      <X className="h-3 w-3 text-slate-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Input
                placeholder="Add a subtask..."
                value={newSubtask}
                onChange={e => setNewSubtask(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddSubtask() }}
                className="h-8 text-sm"
              />
              <Button size="sm" variant="outline" onClick={handleAddSubtask} disabled={!newSubtask.trim()}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-4 border-t border-slate-200">
            <Button
              variant={task.completed ? 'outline' : 'success'}
              size="sm"
              onClick={() => onToggleComplete(task.id)}
            >
              <CheckCircle className="h-4 w-4 mr-1.5" />
              {task.completed ? 'Mark Incomplete' : 'Mark Complete'}
            </Button>
            <div className="flex-1" />
            <Button variant="destructive" size="sm" onClick={() => { onDelete(task.id); onClose(); }}>
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

// --- Main CalendarView ---

export default function CalendarView() {
  const {
    tasks, isLoading, addTask, updateTask, deleteTask, toggleComplete,
  } = useTasks()

  const today = new Date()
  const [viewYear, setViewYear] = useState(today.getFullYear())
  const [viewMonth, setViewMonth] = useState(today.getMonth())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [addDialogDate, setAddDialogDate] = useState<string | undefined>()
  const [selectedTask, setSelectedTask] = useState<PartnerTask | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [enabledCategories, setEnabledCategories] = useState<Set<string>>(new Set(CATEGORIES))

  const categories = useMemo(() => {
    const cats = new Set<string>()
    tasks.forEach(t => { if (t.category) cats.add(t.category) })
    return CATEGORIES.filter(c => cats.has(c))
  }, [tasks])

  const allEnabled = categories.length > 0 && categories.every(c => enabledCategories.has(c))

  const toggleCategory = useCallback((cat: string) => {
    setEnabledCategories(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setEnabledCategories(prev => {
      if (CATEGORIES.every(c => prev.has(c))) return new Set()
      return new Set(CATEGORIES)
    })
  }, [])

  const calendarTasks = useMemo(() => {
    return tasks.filter(t => {
      if (!t.dueDate && !t.startDate) return false
      if (t.category && !enabledCategories.has(t.category)) return false
      if (!t.category && !enabledCategories.has('Other')) return false
      return true
    })
  }, [tasks, enabledCategories])

  const tasksByDate = useMemo(() => {
    const map = new Map<string, PartnerTask[]>()
    for (const task of calendarTasks) {
      const key = task.dueDate ? toDateKeyFromStr(task.dueDate) : task.startDate ? toDateKeyFromStr(task.startDate) : null
      if (!key) continue
      const arr = map.get(key) ?? []
      arr.push(task)
      map.set(key, arr)
    }
    return map
  }, [calendarTasks])

  const taskDateKeys = useMemo(() => new Set(tasksByDate.keys()), [tasksByDate])

  const upcomingTasks = useMemo(() => {
    const now = new Date()
    const future = new Date(now.getTime() + 14 * 86400000)
    return calendarTasks
      .filter(t => {
        const d = t.dueDate ? new Date(t.dueDate) : null
        return d && d >= now && d <= future
      })
      .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
      .slice(0, 6)
  }, [calendarTasks])

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1)
  }
  const goToday = () => {
    const t = new Date()
    setViewYear(t.getFullYear())
    setViewMonth(t.getMonth())
    setSelectedDate(toDateKey(t))
  }

  const handleNavigate = (year: number, month: number) => {
    setViewYear(year)
    setViewMonth(month)
  }

  const handleAddEvent = (dateKey: string) => {
    setAddDialogDate(dateKey)
    setAddDialogOpen(true)
  }

  const handleSaveEvent = (data: EventFormData) => {
    addTask({
      title: data.title,
      description: data.description,
      status: data.status,
      priority: data.priority,
      category: data.category || undefined,
      assigneeName: data.assigneeName || undefined,
      dueDate: data.dueDate || undefined,
      starred: false,
      completed: false,
      tags: [],
      subtasks: [],
    })
  }

  const handleSelectTask = (task: PartnerTask) => {
    setSelectedTask(task)
    setDetailOpen(true)
  }

  const handleDetailUpdate = (id: string, updates: Partial<PartnerTask>) => {
    updateTask(id, updates)
    setSelectedTask(prev => prev && prev.id === id ? { ...prev, ...updates } : prev)
  }

  const syncedTask = useMemo(() => {
    if (!selectedTask) return null
    return tasks.find(t => t.id === selectedTask.id) ?? selectedTask
  }, [selectedTask, tasks])

  if (isLoading) {
    return (
      <div className="flex-1 bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-3 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500 font-medium">Loading calendar...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex overflow-hidden bg-slate-50">
      <CalendarSidebar
        categories={categories}
        enabledCategories={enabledCategories}
        onToggleCategory={toggleCategory}
        onToggleAll={toggleAll}
        allEnabled={allEnabled}
        upcomingTasks={upcomingTasks}
        onSelectDate={(dk) => {
          setSelectedDate(dk)
          const d = new Date(dk)
          if (d.getMonth() !== viewMonth || d.getFullYear() !== viewYear) {
            handleNavigate(d.getFullYear(), d.getMonth())
          }
        }}
        onNewEvent={() => {
          setAddDialogDate(selectedDate ?? toDateKey(new Date()))
          setAddDialogOpen(true)
        }}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        viewYear={viewYear}
        viewMonth={viewMonth}
        selectedDate={selectedDate}
        onNavigate={handleNavigate}
        taskDateKeys={taskDateKeys}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Content header */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-slate-200 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Open sidebar"
          >
            <Menu className="h-5 w-5 text-slate-600" />
          </button>

          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="gap-1.5">
              <Grid3X3 className="h-3.5 w-3.5" />
              Monthly
            </Button>
          </div>

          <Button variant="outline" size="sm" onClick={goToday} className="gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            Today
          </Button>

          <div className="flex-1" />

          <span className="text-sm font-semibold text-slate-700 hidden sm:block">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>

          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon-sm" onClick={prevMonth} aria-label="Previous month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon-sm" onClick={nextMonth} aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Month grid */}
        <div className="flex-1 overflow-y-auto">
          <MonthGrid
            year={viewYear}
            month={viewMonth}
            tasksByDate={tasksByDate}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            onSelectTask={handleSelectTask}
            onAddEvent={handleAddEvent}
          />
        </div>
      </div>

      {/* Add Event Dialog */}
      <AddEventDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleSaveEvent}
        initialDate={addDialogDate}
      />

      {/* Task Detail Sheet */}
      <TaskDetailSheet
        task={syncedTask}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdate={handleDetailUpdate}
        onDelete={deleteTask}
        onToggleComplete={toggleComplete}
      />
    </div>
  )
}
